/**
 * Admin Center – Nutzerverwaltung.
 *
 * Eine Tabelle Person × Bereich. Der eigene Admin-Schalter ist gesperrt, damit
 * sich niemand selbst aussperrt; bei einem Admin sind die Bereichs-Schalter
 * gesperrt, weil ein Admin ohnehin alles sieht. Neue Bereiche erscheinen
 * automatisch als weitere Spalte.
 */
import { useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiFehler } from '../api';
import { useKora } from '../zustand';
import { Modal } from '../komponenten/Modal';
import { Grundeinstellungen } from './EinstellungenModal';
import type { AdminStand, ChecklistenStand } from '../typen';
import './admin.css';

export function AdminCenterModal() {
  const { modalSchliessen, toastZeigen } = useKora();
  const queryClient = useQueryClient();

  const abfrage = useQuery({
    queryKey: ['admin-nutzer'],
    queryFn: () => api.hole<AdminStand>('/admin/nutzer'),
  });

  function neuLaden() {
    void queryClient.invalidateQueries({ queryKey: ['admin-nutzer'] });
    void queryClient.invalidateQueries({ queryKey: ['ich'] });
    void queryClient.invalidateQueries({ queryKey: ['meta'] });
  }

  const recht = useMutation({
    mutationFn: ({ id, bereich, erlaubt }: { id: string; bereich: string; erlaubt: boolean }) =>
      api.aendere(`/admin/nutzer/${id}/recht`, { bereich, erlaubt }),
    onSuccess: neuLaden,
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung nicht möglich.'),
  });

  const adminRecht = useMutation({
    mutationFn: ({ id, istAdmin }: { id: string; istAdmin: boolean }) =>
      api.aendere(`/admin/nutzer/${id}/admin`, { istAdmin }),
    onSuccess: neuLaden,
    onError: (f) => toastZeigen(f instanceof Error ? f.message : 'Änderung nicht möglich.'),
  });

  /**
   * Wartungsläufe.
   *
   * Zweistufig: erst zeigen, was passieren würde, dann ausführen. Diese Läufe
   * fassen hunderte Datensätze an – die Vorschau ist der einzige Moment, in
   * dem ein Fehler noch folgenlos ist.
   */
  /*
   * Reiter statt einer langen Rolle.
   *
   * Es ist jetzt der einzige Ort für alles, was einmal für die Firma
   * festgelegt wird – vorher lag die Hälfte in einem zweiten Fenster
   * „Einstellungen". Untereinander wäre das eine Seite, an deren Ende niemand
   * ankommt.
   */
  const [reiter, setReiter] = useState<'nutzer' | 'konfiguration' | 'pruefung' | 'wartung'>(
    'nutzer',
  );

  const [lauf, setLauf] = useState<string | null>(null);
  const [ausgabe, setAusgabe] = useState('');
  const [ausgefuehrt, setAusgefuehrt] = useState(false);

  const wartungListe = useQuery({
    queryKey: ['wartung'],
    queryFn: () =>
      api.hole<{
        laeufe: { schluessel: string; titel: string; beschreibung: string; gefahr: boolean }[];
      }>(
        '/admin/wartung',
      ),
  });

  const wartung = useMutation({
    mutationFn: ({ schluessel, schreiben }: { schluessel: string; schreiben: boolean }) =>
      api.sende<{ ausgabe: string; code: number }>(`/admin/wartung/${schluessel}`, { schreiben }),
    onSuccess: (r, v) => {
      setAusgabe(r.ausgabe);
      setAusgefuehrt(v.schreiben);
      if (v.schreiben) toastZeigen(r.code === 0 ? 'Lauf abgeschlossen.' : 'Lauf mit Fehler beendet.');
    },
    onError: (f) => {
      setAusgabe(f instanceof Error ? f.message : 'Lauf fehlgeschlagen.');
      setAusgefuehrt(false);
    },
  });

  const d = abfrage.data;
  const spalten = d?.bereiche ?? [];

  return (
    <Modal
      titel="Admin Center"
      untertitel="Alles, was einmal für die Firma festgelegt wird – Zugriff, Kalkulation, Texte, Prüfung."
      breite={900}
      abstandOben={40}
      zeigeSchliessen
      onSchliessen={modalSchliessen}
      kopfZusatz={null}
      fuss={
        <button type="button" className="adm-fertig" onClick={modalSchliessen}>
          Fertig
        </button>
      }
    >
      <div className="adm-reiter">
        {(
          [
            ['nutzer', 'Nutzer & Bereiche'],
            ['konfiguration', 'Konfiguration'],
            ['pruefung', 'Angebotsprüfung'],
            ['wartung', 'Wartung'],
          ] as const
        ).map(([wert, label]) => (
          <button
            key={wert}
            type="button"
            className={`adm-reiter-knopf ${reiter === wert ? 'aktiv' : ''}`}
            onClick={() => setReiter(wert)}
          >
            {label}
          </button>
        ))}
      </div>

      {reiter === 'konfiguration' && <Grundeinstellungen />}
      {reiter === 'pruefung' && <Checklisten />}

      {reiter === 'nutzer' && !d && (
        <div className="modal-hinweis">Nutzer werden geladen …</div>
      )}

      {reiter === 'nutzer' && d && (
        <>
          <div className="adm-tabelle">
            <div
              className="adm-zeile adm-kopf"
              style={{ gridTemplateColumns: `1.7fr repeat(${spalten.length + 1}, .8fr)` }}
            >
              <div>Person</div>
              {spalten.map((s) => (
                <div key={s.key}>{s.kurz}</div>
              ))}
              <div>Admin</div>
            </div>

            {d.nutzer.map((p) => (
              <div
                className="adm-zeile"
                key={p.id}
                style={{ gridTemplateColumns: `1.7fr repeat(${spalten.length + 1}, .8fr)` }}
              >
                <div className="adm-person">
                  <span className="eh-avatar adm-avatar">{p.initialen}</span>
                  <span className="adm-persontext">
                    <span className="adm-name">{p.name}</span>
                    <span className="adm-zugang">{p.zugang}</span>
                  </span>
                </div>

                {spalten.map((s) => {
                  const gesperrt = p.gesperrt.bereiche;
                  return (
                    <button
                      key={s.key}
                      type="button"
                      title={
                        s.label + (gesperrt ? ' (Admin sieht immer alles)' : '')
                      }
                      className={`adm-schalter ${p.rechte[s.key] ? 'an' : ''} ${gesperrt ? 'gesperrt' : ''}`}
                      onClick={() =>
                        !gesperrt &&
                        recht.mutate({ id: p.id, bereich: s.key, erlaubt: !p.rechte[s.key] })
                      }
                    >
                      <span />
                    </button>
                  );
                })}

                <button
                  type="button"
                  title={
                    'Admin · Einstellungen und Rechte' +
                    (p.gesperrt.admin ? ' (eigener Zugang gesperrt)' : '')
                  }
                  className={`adm-schalter ${p.istAdmin ? 'an' : ''} ${p.gesperrt.admin ? 'gesperrt' : ''}`}
                  onClick={() =>
                    !p.gesperrt.admin && adminRecht.mutate({ id: p.id, istAdmin: !p.istAdmin })
                  }
                >
                  <span />
                </button>
              </div>
            ))}
          </div>

          <div className="modal-hinweis">
            Admin sieht immer alle Bereiche und darf Rechte vergeben. Der eigene Admin-Schalter ist
            gesperrt, damit sich niemand selbst aussperrt. Neue Bereiche erscheinen hier automatisch
            als weitere Spalte.
          </div>

          <div className="modal-hinweis">
            Neue Personen bekommen ihren Zugang über das Microsoft-Konto von Eichenherz. Damit die
            Anmeldung greift, muss die E-Mail-Adresse hier hinterlegt sein.
          </div>

        </>
      )}

      {/*
        Wartungsläufe. Sie leben sonst nur in der Kommandozeile – auf dem
        Server gibt es keine, und an die Datenbank der Produktion kommt man
        von außen nicht heran. Ohne diesen Zugang wären sie genau dort
        unerreichbar, wo sie gebraucht werden.
      */}
      {reiter === 'wartung' && (
          <div className="adm-abgleich">
            <div className="adm-abgleich-titel">Wartung</div>
            <div className="adm-abgleich-text">
              Von oben nach unten abarbeiten – so ist die Reihenfolge beim Go-live gedacht. Jeder
              Lauf zeigt zuerst, was er tun würde; geschrieben wird erst mit dem zweiten Klick.
            </div>

            <div className="adm-laeufe">
              {(wartungListe.data?.laeufe ?? []).map((l) => (
                <div
                  className={`adm-lauf ${lauf === l.schluessel ? 'aktiv' : ''} ${l.gefahr ? 'gefahr' : ''}`}
                  key={l.schluessel}
                >
                  <div className="adm-lauf-kopf">
                    <div>
                      <div className="adm-lauf-titel">
                        {l.titel}
                        {l.gefahr && <span className="adm-lauf-marke">Vorsicht</span>}
                      </div>
                      <div className="adm-abgleich-text">{l.beschreibung}</div>
                    </div>
                    {/*
                      „Prüfen", nicht „Vorschau": Der Lauf liest Monday und
                      SharePoint und rechnet – das dauert und ist kein Blick
                      auf ein fertiges Ergebnis. Er schreibt nur nichts.
                    */}
                    <button
                      type="button"
                      className="adm-abgleich-knopf"
                      disabled={wartung.isPending}
                      onClick={() => {
                        setLauf(l.schluessel);
                        setAusgabe('');
                        setAusgefuehrt(false);
                        wartung.mutate({ schluessel: l.schluessel, schreiben: false });
                      }}
                    >
                      {wartung.isPending && lauf === l.schluessel ? 'Prüft …' : 'Prüfen'}
                    </button>
                  </div>

                  {lauf === l.schluessel && ausgabe && (
                    <>
                      <pre className="adm-ausgabe">{ausgabe}</pre>
                      {!ausgefuehrt && (
                        <button
                          type="button"
                          className={`adm-abgleich-knopf betont ${l.gefahr ? 'gefahr' : ''}`}
                          disabled={wartung.isPending}
                          onClick={() => {
                            /*
                              Ein Lauf, der löscht, verdient eine Rückfrage, die
                              man nicht wegklickt, ohne sie gelesen zu haben.
                              Die übrigen Läufe legen an oder tragen nach – ein
                              Fehlgriff dort ist ärgerlich, aber nicht endgültig.
                            */
                            if (
                              l.gefahr &&
                              !window.confirm(
                                'Alle Angebote, Kunden und Verläufe aus Kora löschen?\n\n' +
                                  'Monday und SharePoint bleiben unberührt. Der Schritt lässt sich ' +
                                  'nicht rückgängig machen.',
                              )
                            ) {
                              return;
                            }
                            wartung.mutate({ schluessel: l.schluessel, schreiben: true });
                          }}
                        >
                          {l.gefahr ? 'Endgültig löschen' : 'Ausführen'}
                        </button>
                      )}
                      {ausgefuehrt && <div className="adm-abgleich-text">Ausgeführt.</div>}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>

      )}
    </Modal>
  );
}

/* ------------------------------------------------------------------------- */

/**
 * Prüf-Checklisten je Gewerk.
 *
 * Eine Fassung wird nie gelöscht, nur abgelöst: Prüfungen verweisen auf die
 * Fassung, mit der sie gelaufen sind. Ohne sie ließe sich ein alter Prüfbericht
 * später nicht mehr lesen, weil die Kriterien inzwischen andere sind.
 */
function Checklisten() {
  const queryClient = useQueryClient();
  const { toastZeigen } = useKora();
  const [fehler, setFehler] = useState('');
  const [laeuft, setLaeuft] = useState('');

  const abfrage = useQuery({
    queryKey: ['checklisten'],
    queryFn: () => api.hole<ChecklistenStand>('/pruefung/checklisten'),
  });

  const hochladen = useMutation({
    mutationFn: ({ prefix, datei }: { prefix: string; datei: File }) => {
      const formular = new FormData();
      formular.append('kategorie', prefix);
      formular.append('datei', datei);
      return api.datei<{ toast: string }>('/pruefung/checklisten', formular);
    },
    onSuccess: (a) => {
      setFehler('');
      setLaeuft('');
      void queryClient.invalidateQueries({ queryKey: ['checklisten'] });
      toastZeigen(a.toast);
    },
    onError: (f) => {
      setLaeuft('');
      setFehler(f instanceof ApiFehler ? f.message : 'Die Datei konnte nicht abgelegt werden.');
    },
  });

  const daten = abfrage.data;

  return (
    <div className="adm-block">
      <div className="adm-titel">Prüf-Checklisten</div>
      <div className="adm-hinweis">
        Je Gewerk eine PDF mit den Punkten, auf die geprüft werden soll. Kora legt sie beim Prüfen
        zusammen mit dem Angebot vor. Ohne Checkliste bleibt die Statussperre für dieses Gewerk
        aus – ein Vorgang, den niemand prüfen kann, darf nicht blockiert sein.
      </div>

      {daten && !daten.kiAktiv && (
        <div className="adm-hinweis" style={{ color: 'var(--eh-gelb-text)' }}>
          Der Zugang zur Prüfung ist noch nicht hinterlegt (ANTHROPIC_API_KEY). Bis dahin lässt sich
          nichts prüfen.
        </div>
      )}

      <div className="adm-checklisten">
        {(daten?.kategorien ?? []).map((k) => (
          <div className="adm-checkliste" key={k.prefix}>
            <div className="adm-checkliste-kopf">
              <span className="adm-checkliste-name">
                {k.prefix} · {k.label}
              </span>
              <label className="adm-abgleich-knopf">
                {laeuft === k.prefix ? 'Wird abgelegt …' : k.aktuell ? 'Neue Fassung' : 'PDF wählen'}
                <input
                  type="file"
                  accept="application/pdf"
                  style={{ display: 'none' }}
                  disabled={hochladen.isPending}
                  onChange={(e) => {
                    const datei = e.target.files?.[0];
                    e.target.value = '';
                    if (!datei) return;
                    setFehler('');
                    setLaeuft(k.prefix);
                    hochladen.mutate({ prefix: k.prefix, datei });
                  }}
                />
              </label>
            </div>

            {k.aktuell ? (
              <div className="adm-checkliste-stand">
                <a
                  href={`/api/pruefung/checklisten/${k.aktuell.id}/pdf`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {k.aktuell.dateiname}
                </a>
                <span>
                  seit {new Date(k.aktuell.hochgeladenAm).toLocaleDateString('de-AT')} ·{' '}
                  {k.aktuell.hochgeladenVon}
                  {k.fassungen.length > 1 ? ` · ${k.fassungen.length} Fassungen` : ''}
                </span>
              </div>
            ) : (
              <div className="adm-checkliste-stand leer">noch keine Checkliste</div>
            )}
          </div>
        ))}
      </div>

      {fehler && <div className="eh-fehler">{fehler}</div>}
    </div>
  );
}
